'use client';

import VideoComponent from '@/components/hero/VideoComponent';
import SvgRenderer from '@/components/shared/svg_renderer';
import { useSizeModal } from '@/helpers/size_guide_modal';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import useColorContrast from '@/hooks/useColorContrast';
import { SectionItem } from '@/types/product_page/productType';
import { usePostHog } from 'posthog-js/react';
import { getThemeClass } from '@/helpers/theme';
import styles from './advanced_features_slide.module.css';
import { generateDirectusSrc, generateDirectusSrcSet } from '@/helpers/image';

const FeatureElem = ({
  icon,
  name,
  description,
  isLight,
  theme = 'dark',
}: {
  icon: string;
  name: string;
  description: string;
  isLight: boolean;
  index: number;
  theme?: 'dark' | 'light';
}) => {
  return (
    <div
      className={`flex flex-col gap-8 md:gap-6 rounded-xl p-6 md:p-8 h-full ${getThemeClass(theme, 'background')}`}
    >
      <SvgRenderer
        svgText={icon}
        className="w-12 h-12 flex-shrink-0"
        color={theme === 'light' ? '#000' : '#fff'}
      />
      <div className="flex flex-col gap-2 h-full">
        <h4 className={`font-bold text-2xl ${getThemeClass(theme, 'heading')}`}>
          {name}
        </h4>
        <p
          className={`font-proxima_font opacity-80 ${getThemeClass(theme, 'text')}`}
        >
          {description}
        </p>
      </div>
    </div>
  );
};

const FeatureDifference = ({ data }: { data: SectionItem }) => {
  const { isSizeModalOpen } = useSizeModal();
  const posthog = usePostHog();
  const mediaTypes = [
    `${data?.primary_video || data?.primary_youtube_url ? 'video' : 'image'}`,
    `${data?.secondary_video || data?.secondary_youtube_url ? 'video' : 'image'}`,
  ];
  const [showDifferences, setShowDifferences] = useState(
    data?.product_difference_on
  );

  const isLight = useColorContrast(data.bg_color || '#000');
  const theme = data?.theme || 'dark';

  const handleToggle = () => {
    setShowDifferences(!showDifferences);
    posthog?.capture('PFC_Toggle_Button_Click');
  };

  return (
    <div
      id="compare"
      className={`w-full flex items-center justify-center ${getThemeClass(theme, 'background')} py-8 md:py-14 px-4`}
    >
      <div className={'max-w-7xl w-full flex flex-col items-center'}>
        {/* Section heading */}
        <h1
          className={`text-3xl lg:text-4.5xl ${getThemeClass(theme, 'heading')} font-bold text-center mb-8`}
        >
          {data.heading}
        </h1>

        <div className={`w-full min-h-[10rem] md:min-h-[3rem]`}>
          <p
            className={`font-medium transition-all duration-500 text-[16px] ${getThemeClass(theme, 'text')} font-proxima_font text-center max-w-screen-md mx-auto`}
          >
            {data.description}
          </p>

          {/* Comparison switch */}
          <div className="flex flex-col gap-3 items-center justify-center py-8">
            <p
              className={`font-proxima_font font-semibold ${getThemeClass(theme, 'heading')}`}
            >
              See the difference
            </p>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                onChange={handleToggle}
                checked={showDifferences}
                type="checkbox"
                value=""
                className="sr-only peer "
              />
              <div className="relative flex items-center justify-between p-[4px] w-[70px] h-8 bg-red-500 peer-focus:outline-none peer-checked:bg-[#40C4AA] rounded-full peer peer-checked:after:translate-x-[38px] after:content-[''] after:absolute after:border after:border-[#dbdcdf]  after:bg-white after:shadow-xl after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 ">
                <div
                  className={cn(
                    'text-white text-sm font-inter_font font-bold absolute',
                    showDifferences ? 'left-[12px]' : 'right-2'
                  )}
                >
                  {showDifferences ? 'ON' : 'OFF'}
                </div>
              </div>
            </label>
          </div>
        </div>

        <div
          className={cn(
            `relative aspect-[4/3] lg:aspect-auto sm:h-96 lg:h-[650px] w-full rounded-[10px]  overflow-hidden `,
            isSizeModalOpen && 'z-[-1] '
          )}
        >
          {[false, true].map((value, index) => {
            const desktopId =
              index === 1 ? data.secondary_banner : data.primary_banner;
            const mobileId =
              index === 1 ? data.mobile_secondary : data.mobile_primary;

            const rawVideo =
              index === 1 ? data?.secondary_video : data?.primary_video;
            const video_url = (
              rawVideo && typeof rawVideo === 'object'
                ? rawVideo.id
                : rawVideo ?? ''
            ) as string;
            const mux_playback_id =
              rawVideo && typeof rawVideo === 'object'
                ? rawVideo.mux_playback_id
                : null;

            const youtube_url = `${index === 1 ? data?.secondary_youtube_url : data?.primary_youtube_url}`;

            const rawMobileVideo =
              index === 1
                ? data?.mobile_secondary_video
                : data?.mobile_primary_video;
            const mobile_src = (
              rawMobileVideo && typeof rawMobileVideo === 'object'
                ? rawMobileVideo.id
                : rawMobileVideo ?? ''
            ) as string;
            const mobile_mux_playback_id =
              rawMobileVideo && typeof rawMobileVideo === 'object'
                ? rawMobileVideo.mux_playback_id
                : null;

            const thumbnail =
              index === 1
                ? data.secondary_thumbnail ?? null
                : data.primary_thumbnail ?? null;

            return mediaTypes[index] === 'image' ? (
              value === showDifferences ? (
                <picture key={index}>
                  {data.mobile_primary && data.mobile_secondary && (
                    <source
                      media="(max-width: 639px)"
                      srcSet={generateDirectusSrcSet(mobileId)}
                      sizes="(max-width: 639px) 100vw, 640px"
                    />
                  )}
                  <source
                    media="(min-width: 640px)"
                    srcSet={generateDirectusSrcSet(desktopId)}
                    sizes="(min-width: 640px) 100vw, 1200px"
                  />
                  <img
                    alt={data?.heading}
                    src={generateDirectusSrc(desktopId, 1200)}
                    srcSet={generateDirectusSrcSet(desktopId)}
                    sizes="(max-width: 768px) 100vw, 1200px"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover bg-cover bg-center"
                  />
                </picture>
              ) : null
            ) : value === showDifferences ? (
              <div
                key={mux_playback_id ?? video_url ?? index}
                className="absolute w-full h-full object-cover bg-cover bg-center"
              >
                <VideoComponent
                  key={mux_playback_id ?? video_url ?? index}
                  file={video_url}
                  loop={data?.loop}
                  autoplay={data?.autoplay}
                  youtube_url={youtube_url === 'null' ? null : youtube_url}
                  mobile_file={mobile_src === 'null' ? null : mobile_src}
                  thumbnail={thumbnail}
                  mux_playback_id={mux_playback_id}
                  mobile_mux_playback_id={mobile_mux_playback_id}
                />
              </div>
            ) : null;
          })}
        </div>

        {data.cards && data.cards?.length > 0 && (
          <>
            <div
              className={`hidden w-full mx-auto py-6 lg:py-12 md:grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-6`}
            >
              {data.cards?.map(({ icon, title, description }, index) => {
                if (!title || !description) return;
                return (
                  <FeatureElem
                    key={index}
                    icon={icon as string}
                    name={title}
                    description={description}
                    isLight={isLight}
                    index={index}
                    theme={theme}
                  />
                );
              })}
            </div>

            <div className="w-screen -mx-4 md:hidden">
              <Swiper
                loop={data.cards && data.cards.length > 2}
                slidesPerView={1.19}
                centeredSlides={true}
                spaceBetween={8}
                pagination={{
                  clickable: true,
                }}
                modules={[Navigation, Thumbs, Pagination]}
                className={`${styles.features_swiper} md:hidden w-full !py-6 ${getThemeClass(theme, 'background')}`}
                navigation={true}
              >
                {data.cards?.map(({ icon, title, description }, index) => {
                  if (!title || !description) return;
                  return (
                    <SwiperSlide key={index} className="rounded-xl">
                      <FeatureElem
                        key={index}
                        icon={icon as string}
                        name={title}
                        description={description}
                        isLight={isLight}
                        index={index}
                        theme={theme}
                      />
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeatureDifference;
